import { Link } from 'react-router-dom';
import mediaUrl from '../../service/mediaUrl';
import { excerpt } from '../../utils/text';
import { formatPublicationTime } from './functions';
import { getCalendarPillStyle } from './pillStyles';
import { CalendarGranularity, CalendarPublication } from './types';
import nfThumb from '../../assets/nf_thumb.svg';

type InlinePublicationCardProps = Readonly<{
    publication: CalendarPublication;
    index: number;
    variant: CalendarGranularity;
    fillCell?: boolean;
}>;

const CARD_CLASS = 'flex w-full gap-1 rounded-sm p-1';
const THUMBNAIL_CLASS = 'shrink-0 rounded-md object-cover';
const TITLE_ROW_CLASS = 'flex-1 text-[11px] font-medium leading-snug';
const TITLE_CLAMP_BY_VARIANT: Record<CalendarGranularity, string> = {
    [CalendarGranularity.Month]: 'line-clamp-3',
    [CalendarGranularity.Week]: 'line-clamp-4',
    [CalendarGranularity.Day]: 'line-clamp-7',
};
const TITLE_MAX_LENGTH_BY_VARIANT: Record<CalendarGranularity, number> = {
    [CalendarGranularity.Month]: 50,
    [CalendarGranularity.Week]: 40,
    [CalendarGranularity.Day]: 300,
};
const TITLE_FILL_CELL_CLAMP_BY_VARIANT: Record<
    Extract<CalendarGranularity, CalendarGranularity.Week | CalendarGranularity.Day>,
    string
> = {
    [CalendarGranularity.Week]: 'line-clamp-5',
    [CalendarGranularity.Day]: 'line-clamp-10',
};
const TITLE_FILL_CELL_MAX_LENGTH_BY_VARIANT: Record<
    Extract<CalendarGranularity, CalendarGranularity.Week | CalendarGranularity.Day>,
    number
> = {
    [CalendarGranularity.Week]: 150,
    [CalendarGranularity.Day]: 450,
};

function getTitleDisplay(
    variant: CalendarGranularity,
    fillCell: boolean,
): Readonly<{ titleClassName: string; maxLength: number }> {
    if (fillCell && (variant === CalendarGranularity.Week || variant === CalendarGranularity.Day)) {
        return {
            titleClassName: `${TITLE_ROW_CLASS} break-words ${TITLE_FILL_CELL_CLAMP_BY_VARIANT[variant]}`,
            maxLength: TITLE_FILL_CELL_MAX_LENGTH_BY_VARIANT[variant],
        };
    }

    return {
        titleClassName: `${TITLE_ROW_CLASS} break-words ${TITLE_CLAMP_BY_VARIANT[variant]}`,
        maxLength: TITLE_MAX_LENGTH_BY_VARIANT[variant],
    };
}
const VIEW_LINK_CLASS =
    'flex justify-center rounded-full border border-solid border-primary bg-primary py-0.5 px-2 text-[11px] font-medium text-dark-gray';

export function InlinePublicationCard({
    publication,
    index,
    variant,
    fillCell = false,
}: InlinePublicationCardProps) {
    const { titleClassName, maxLength: titleMaxLength } = getTitleDisplay(variant, fillCell);
    const title = excerpt(publication.title, titleMaxLength);

    return (
        <article className={`${CARD_CLASS} ${getCalendarPillStyle(index)}`}>
            <img
                src={mediaUrl(publication)}
                alt={publication.title}
                className={`${THUMBNAIL_CLASS} w-1/2`}
            />
            <div className="flex">
                <div className="flex flex-col">
                    <p className={titleClassName}>
                        <span className="opacity-80">
                            {formatPublicationTime(publication.publicationDate)}
                        </span>
                        {' - '}
                        {title}
                    </p>
                    <div className={`flex shrink-0 justify-center`}>
                        <Link
                            to={`/show_publication/${publication.id}`}
                            className={VIEW_LINK_CLASS}
                        >
                            Voir
                        </Link>
                    </div>
                </div>

                {publication.isGlobal ? (
                    <img
                        src={nfThumb}
                        alt="Global"
                        className="ml-auto mt-px h-3 w-3 shrink-0 self-start"
                    />
                ) : null}
            </div>
        </article>
    );
}
